import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  AveragePlayerScore,
  AverageStat,
} from '@sorare/core/src/__generated__/globalTypes';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import { BreakdownPlayerScoreCell } from './BreakdownPlayerScoreCell/index.tsx';
import { ProjectionCell } from './ProjectionCell/index.tsx';
import { StatCell } from './StatCell/index.tsx';
import type { SearchPlayerStatsCells_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: SearchPlayerStatsCells_anyPlayer;
  scopedBreakdowns?: Partial<Record<AveragePlayerScore, number | null>>;
  scopedFootballAverageStats?: readonly {
    stat: AverageStat;
    value: number | null;
  }[];
  scopedFootballTotalStats?: readonly {
    stat: AverageStat;
    value: number | null;
  }[];
};

export const SearchPlayerStatsCells = ({
  anyPlayer,
  scopedBreakdowns,
  scopedFootballAverageStats,
  scopedFootballTotalStats,
}: Props) => {
  const { statsManager, displayableStats } = useManagePlayerSearchView();

  return (
    <>
      {statsManager[PlayerSearchViewAttributes.BREAKDOWN].map(b => (
        <BreakdownPlayerScoreCell
          key={b}
          breakdown={b}
          anyPlayer={anyPlayer}
          scopedValue={
            scopedBreakdowns ? (scopedBreakdowns[b] ?? null) : undefined
          }
        />
      ))}
      {statsManager[PlayerSearchViewAttributes.PROJECTION].map(
        so5FixtureEvent => (
          <ProjectionCell
            key={so5FixtureEvent}
            anyPlayer={anyPlayer}
            so5FixtureEvent={so5FixtureEvent}
          />
        )
      )}
      {displayableStats.map(stat => (
        <StatCell
          key={stat}
          stat={stat}
          anyPlayer={anyPlayer}
          scopedFootballAverageStats={scopedFootballAverageStats}
          scopedFootballTotalStats={scopedFootballTotalStats}
        />
      ))}
    </>
  );
};

SearchPlayerStatsCells.fragments = {
  anyPlayer: gql`
    fragment SearchPlayerStatsCells_anyPlayer on AnyPlayerInterface {
      slug
      ...StatCell_anyPlayer
      ...BreakdownPlayerScoreCell_anyPlayer
      ...ProjectionCell_anyPlayer
    }
    ${StatCell.fragments.anyPlayer}
    ${BreakdownPlayerScoreCell.fragments.anyPlayer}
    ${ProjectionCell.fragments.anyPlayer}
  ` as TypedDocumentNode<SearchPlayerStatsCells_anyPlayer>,
};
